import { useEffect, useRef, useState } from 'react';
import { apiFetch, readErrorMessage } from '../api/client';
import { previewFileUrl } from '../fileBrowser/fileBrowserApi';
import { convertHeicBlobToJpeg, isHeicPath } from './heicConversion';

export type MediaImageDisplayState = 'idle' | 'loading' | 'ready' | 'error';

export interface MediaImageDisplayResult {
  displaySrc: string;
  state: MediaImageDisplayState;
  /** Set when state is error after a preview fetch (e.g. 403/404). */
  errorStatus: number | null;
  errorMessage: string | null;
}

/**
 * Loads preview bytes via the files API so access errors surface as HTTP status.
 * HEIC is converted to JPEG client-side; other types use a blob object URL.
 */
export function useMediaImageDisplaySrc(
  mountId: string,
  relativePath: string,
  enabled: boolean
): MediaImageDisplayResult {
  const [displaySrc, setDisplaySrc] = useState('');
  const [state, setState] = useState<MediaImageDisplayState>('idle');
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !mountId || !relativePath) {
      setDisplaySrc('');
      setState('idle');
      setErrorStatus(null);
      setErrorMessage(null);
      return;
    }

    let cancelled = false;

    const revokeObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    const run = async () => {
      setState('loading');
      setDisplaySrc('');
      setErrorStatus(null);
      setErrorMessage(null);
      revokeObjectUrl();

      try {
        const res = await apiFetch(previewFileUrl(mountId, relativePath));
        if (!res.ok) {
          const message = await readErrorMessage(res);
          if (!cancelled) {
            setErrorStatus(res.status);
            setErrorMessage(message);
            setState('error');
          }
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;

        if (isHeicPath(relativePath)) {
          const jpegBlob = await convertHeicBlobToJpeg(blob);
          if (cancelled) return;
          const url = URL.createObjectURL(jpegBlob);
          objectUrlRef.current = url;
          setDisplaySrc(url);
          setState('ready');
          return;
        }

        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setDisplaySrc(url);
        setState('ready');
      } catch (error) {
        if (!cancelled) {
          setErrorStatus(null);
          setErrorMessage(error instanceof Error ? error.message : 'The preview request failed.');
          setState('error');
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      revokeObjectUrl();
    };
  }, [mountId, relativePath, enabled]);

  return { displaySrc, state, errorStatus, errorMessage };
}
